import * as d3 from 'd3';

/** Same palette as CalendarAnalyticsDashboard surgery-type pie chart. */
export const SURGERY_TYPE_PALETTE = d3.schemeCategory10;

/** Expanded strata procedure keys → display labels (joint_decisions_strata / Spanner). */
export const SPREADSHEET_SURGERY_DISPLAY_LABELS = {
  cervical_epidural_injection: 'Epidural Injection',
  thoracic_epidural_injection: 'Epidural Injection',
  lumbar_epidural_injection: 'Epidural Injection',
  cervical_laminectomy: 'Laminectomy',
  thoracic_laminectomy: 'Laminectomy',
  lumbar_laminectomy: 'Laminectomy',
  cervical_hemilaminotomy_foraminotomy: 'Hemilaminotomy/Foraminotomy',
  lumbar_hemilaminotomy_foraminotomy: 'Hemilaminotomy/Foraminotomy',
  hemilaminotomy_micro_discectomy: 'Hemilaminotomy w/ Micro Discectomy',
  hemilaminotomy_micro_discectomy_barricade: 'Hemilaminotomy w/ Micro Discectomy w/ Barricade',
  acdf: 'ACDF',
  in_situ_fusion: 'In Situ Fusion',
  cervical_psf_instrumentation: 'PSF w/ Instrumentation',
  thoracic_psf_instrumentation: 'PSF w/ Instrumentation',
  lumbar_psf_instrumentation: 'PSF w/ Instrumentation',
  standalone_alif: 'Standalone ALIF',
  alif_with_psf: 'ALIF w/ PSF',
  olif: 'OLIF',
  xlif: 'XLIF',
  plif: 'PLIF',
  tlif: 'TLIF',
  cervical_laminoplasty: 'Laminoplasty',
  lumbar_facet_joint_replacement: 'Facet Joint Replacement',
  lumbar_coflex: 'Coflex',
  cervical_arthroplasty: 'Arthroplasty',
  lumbar_arthroplasty: 'Arthroplasty',
  cervical_scs: 'SCS',
  thoracic_scs: 'SCS',
  lumbar_scs: 'SCS',
};

/** Display label for a procedure key (always region-neutral; region is shown separately). */
export function getProcedureDisplayLabel(procedureKey) {
  if (!procedureKey) return '';
  const mapped = SPREADSHEET_SURGERY_DISPLAY_LABELS[procedureKey];
  if (mapped) return mapped;
  return String(procedureKey).replace(/_/g, ' ');
}

/**
 * Preferred top-to-bottom order in the Joint Decisions procedure key,
 * grouped by anatomical region.
 */
export const PROCEDURE_KEY_DISPLAY_ORDER = [
  // Cervical — levelsless first, then least → more invasive
  'cervical_epidural_injection',
  'cervical_scs',
  'cervical_arthroplasty',
  'acdf',
  'cervical_hemilaminotomy_foraminotomy',
  'cervical_laminectomy',
  'cervical_laminoplasty',
  'cervical_psf_instrumentation',
  // Thoracic
  'thoracic_epidural_injection',
  'thoracic_scs',
  'thoracic_laminectomy',
  'thoracic_psf_instrumentation',
  // Lumbar
  'lumbar_epidural_injection',
  'lumbar_scs',
  'lumbar_hemilaminotomy_foraminotomy',
  'lumbar_laminectomy',
  'hemilaminotomy_micro_discectomy',
  'hemilaminotomy_micro_discectomy_barricade',
  'lumbar_coflex',
  'lumbar_facet_joint_replacement',
  'in_situ_fusion',
  'lumbar_psf_instrumentation',
  'lumbar_arthroplasty',
  'standalone_alif',
  'alif_with_psf',
  'olif',
  'xlif',
  'tlif',
  'plif',
];

const PROCEDURE_KEY_ORDER_INDEX = new Map(
  PROCEDURE_KEY_DISPLAY_ORDER.map((key, index) => [key, index]),
);

/** Sort comparator for procedure keys in the Joint Decisions key list. */
export function compareProcedureKeys(aKey, bKey) {
  const aIdx = PROCEDURE_KEY_ORDER_INDEX.has(aKey)
    ? PROCEDURE_KEY_ORDER_INDEX.get(aKey)
    : Number.MAX_SAFE_INTEGER;
  const bIdx = PROCEDURE_KEY_ORDER_INDEX.has(bKey)
    ? PROCEDURE_KEY_ORDER_INDEX.get(bKey)
    : Number.MAX_SAFE_INTEGER;
  if (aIdx !== bIdx) return aIdx - bIdx;
  return String(aKey || '').localeCompare(String(bKey || ''));
}

/** procedure_key → anatomical region (matches server jointdec_strata_constants.PROCEDURE_REGION). */
export function procedureRegion(procedureKey) {
  if (!procedureKey) return 'LUMBAR';
  if (procedureKey === 'acdf' || procedureKey.startsWith('cervical_')) return 'CERVICAL';
  if (procedureKey.startsWith('thoracic_')) return 'THORACIC';
  return 'LUMBAR';
}

const LEGACY_SPREADSHEET_SURGERY_DISPLAY_LABELS = {
  // Legacy v2 keys (kept for older references)
  DECOMPRESSION: 'Decompression',
  FUSION: 'Fusion',
  'DYNAMIC STABILIZATION IMPLANT': 'Dynamic Stabilization Implant',
  ARTHROPLASTY: 'Arthroplasty',
  LAMINOPLASTY: 'Laminoplasty',
  SCS: 'SCS',
  COFLEX: 'Coflex',
  'FACET JOINT REPLACEMENT': 'Facet Joint Replacement',
};

Object.assign(SPREADSHEET_SURGERY_DISPLAY_LABELS, LEGACY_SPREADSHEET_SURGERY_DISPLAY_LABELS);

/** Stable domain for joint-decisions procedure columns (alphabetical). */
const SPREADSHEET_SURGERY_COLOR_DOMAIN = [
  ...new Set(Object.values(SPREADSHEET_SURGERY_DISPLAY_LABELS)),
].sort((a, b) => a.localeCompare(b));

/**
 * Explicit colors for alphabetical left-to-right chart order.
 * Adjacent labels prefer distant hues for contrast; named prefs override as requested.
 */
const SURGERY_LABEL_COLORS = {
  ACDF: '#1f77b4', // blue
  'ALIF w/ PSF': '#ff7f0e', // orange
  Arthroplasty: '#17becf', // cyan
  Coflex: '#7dce7d', // lighter green
  Decompression: '#e377c2', // pink
  'Dynamic Stabilization Implant': '#8c564b', // brown
  'Epidural Injection': '#d62728', // red
  'Facet Joint Replacement': '#f58518', // amber-orange
  Fusion: '#4e79a7', // steel blue
  'Hemilaminotomy w/ Micro Discectomy': '#1f77b4', // blue
  'Hemilaminotomy w/ Micro Discectomy w/ Barricade': '#ffe34a', // yellow
  'Hemilaminotomy/Foraminotomy': '#9467bd', // purple
  'In Situ Fusion': '#8c564b', // brown
  Laminectomy: '#0d3d0d', // very dark green
  Laminoplasty: '#4c78a8', // blue
  OLIF: '#f58518', // orange
  PLIF: '#54a24b', // green
  'PSF w/ Instrumentation': '#800020', // dark red / maroon
  SCS: '#17becf', // cyan (between purple and yellow)
  'Standalone ALIF': '#e377c2', // pink
  TLIF: '#e377c2', // pink
  XLIF: '#8c564b', // brown
};

/** Fallback ordinal palette when a label is not in SURGERY_LABEL_COLORS. */
const HIGH_CONTRAST_FALLBACK_PALETTE = [
  '#1f77b4', // blue
  '#ff7f0e', // orange
  '#2ca02c', // green
  '#e377c2', // pink
  '#17becf', // cyan
  '#8c564b', // brown
  '#9467bd', // purple
  '#f58518', // amber
  '#e15759', // coral
  '#76b7b2', // teal
  '#59a14f', // green
  '#4e79a7', // steel
  '#b07aa1', // mauve
  '#ffe34a', // yellow
  '#d62728', // red
];

const spreadsheetSurgeryColorScale = d3
  .scaleOrdinal(HIGH_CONTRAST_FALLBACK_PALETTE)
  .domain(SPREADSHEET_SURGERY_COLOR_DOMAIN);

/** Normalize OPM / patient procedure strings (matches analytics pie labels). */
export function normalizeSurgeryProcedureLabel(label) {
  if (label == null || label === '') return 'Unknown';
  const text = String(label).trim();
  const fromKey = SPREADSHEET_SURGERY_DISPLAY_LABELS[text] || SPREADSHEET_SURGERY_DISPLAY_LABELS[text.toUpperCase()];
  if (fromKey) return fromKey;
  return text.replace(/_/g, ' ');
}

/** Build a scale for a set of procedure labels (e.g. upcoming surgeries in a date range). */
export function buildSurgeryTypeColorScale(labels) {
  const normalized = labels.map(normalizeSurgeryProcedureLabel);
  const domain = [
    ...new Set([...SPREADSHEET_SURGERY_COLOR_DOMAIN, ...normalized]),
  ].sort((a, b) => a.localeCompare(b));
  return d3.scaleOrdinal(HIGH_CONTRAST_FALLBACK_PALETTE).domain(domain);
}

/**
 * Color for a surgery type label (analytics pie, badges, joint-decisions charts).
 * Known labels use a fixed high-contrast map tuned for alphabetical adjacency.
 * Pass `labels` for unknown upcoming-surgery names so they share the same ordinal scheme.
 */
export function getSurgeryTypeColor(labelOrKey, options = {}) {
  const normalized = normalizeSurgeryProcedureLabel(labelOrKey);
  if (SURGERY_LABEL_COLORS[normalized]) {
    return SURGERY_LABEL_COLORS[normalized];
  }
  if (SPREADSHEET_SURGERY_COLOR_DOMAIN.includes(normalized)) {
    return spreadsheetSurgeryColorScale(normalized);
  }
  const { labels } = options;
  if (labels?.length) {
    const scale = buildSurgeryTypeColorScale(labels);
    return scale(normalized) ?? HIGH_CONTRAST_FALLBACK_PALETTE[0];
  }
  return HIGH_CONTRAST_FALLBACK_PALETTE[0];
}

/** Joint Decisions spreadsheet bar chart / procedure chips. */
export function getSpreadsheetSurgeryColor(surgeryKey) {
  if (!surgeryKey) return '#6c757d';
  const display = SPREADSHEET_SURGERY_DISPLAY_LABELS[surgeryKey];
  return getSurgeryTypeColor(display || surgeryKey);
}
